import { useEffect, useMemo } from "react";
import { Controller, useForm } from "react-hook-form";
import { ArchiveIcon, ArrowLeft, ChevronRight, ImageIcon, LayoutGrid, Plus, Save, Trash2, X } from "lucide-react";
import { LightboxProvider, useLightboxContext } from "@/modern/components/ui/lightbox";
import { Button } from "@/modern/components/ui/button-shadcn";
import { cn } from "@/modern/utils";
import { MarketingWorkFile } from "@/bryntum-lib/models/MarketingWorkFile";
import { MarketingWorkFileCard } from "@/bryntum-lib/models/MarketingWorkFileCard";
import { MarketingAsset } from "@/bryntum-lib/models/MarketingAsset";
import { SelectorImage } from "@/react/components/entityViews/files/components/editors/FileSelectorEditor/FileSelectorModal";
import { useSwivuBryntumContext } from "@/react/hooks/useSwivuBryntumContext";
import { useSwivuStore } from "@/react/hooks/useSwivuStore";
import { STORE_KEYS } from "@/other/constants/StoreKeys";
import { EDITOR_KEYS } from "@/other/constants/EditorKeys";

// The shape exposed to callers via onSave — unchanged from before.
export type CardFormState = {
  heading: string;
  subHeading: string;
  bodyText: string;
};

// Internal RHF schema — assets are owned by the form until Save.
type CardFormValues = {
  heading: string;
  subHeading: string;
  bodyText: string;
  assets: number[];
};

interface WorkFileLivePreviewProps {
  card: MarketingWorkFileCard | null;
  isNewCard?: boolean;
  onSave?: (data: CardFormState) => void;
  onDelete?: () => void;
  onCancel?: () => void;
  /** Workfile-level context — forwarded from WorkFileDetailView */
  file?: MarketingWorkFile;
  hubLabel?: string | null;
  onBack?: () => void;
}

const escapeHtml = (s: string) =>
  s
    .replace(/&/g, "&amp;")
    .replace(/</g, "&lt;")
    .replace(/>/g, "&gt;")
    .replace(/"/g, "&quot;")
    .replace(/'/g, "&#39;");

export const WorkFileLivePreview = ({
  card,
  isNewCard = false,
  onSave,
  onDelete,
  onCancel,
  file,
  hubLabel: _hubLabel,
  onBack,
}: WorkFileLivePreviewProps) => {
  const { project, editorHandler } = useSwivuBryntumContext();

  // Subscribe to asset store so resolved thumbnails update reactively.
  const { version: assetVersion } = useSwivuStore({ storeId: STORE_KEYS.marketingAssetStore });

  // -------------------------------------------------------------------------
  // React Hook Form
  // -------------------------------------------------------------------------
  const {
    control,
    handleSubmit,
    reset,
    setValue,
    watch,
    formState: { isDirty, isValid },
  } = useForm<CardFormValues>({
    defaultValues: {
      heading: "",
      subHeading: "",
      bodyText: "",
      assets: [],
    },
    // Validate on every keystroke so errors appear / clear immediately.
    mode: "onChange",
  });

  // Sync form state whenever the selected card changes.
  // The `key` on the <article> remounts contentEditable DOM; reset() syncs RHF.
  useEffect(() => {
    reset({
      heading: card?.heading ?? "",
      subHeading: card?.subHeading ?? "",
      bodyText: card?.bodyText ?? "",
      assets: card?.assets ?? [],
    });
  }, [card?.id]);

  // -------------------------------------------------------------------------
  // Assets — driven by form state, not card.assets directly
  // -------------------------------------------------------------------------
  const watchedAssets = watch("assets");

  const linkedAssets = useMemo<MarketingAsset[]>(() => {
    if (!watchedAssets?.length || !project) return [];
    const assetStore = project.getCrudStore(STORE_KEYS.marketingAssetStore);
    if (!assetStore) return [];
    return watchedAssets
      .map((id: number) => assetStore.getById(id) as unknown as MarketingAsset | undefined)
      .filter((a): a is MarketingAsset => Boolean(a));
  }, [watchedAssets, project, assetVersion]);

  // Lightbox images — index-aligned with linkedAssets so each tile can open at
  // the correct position. Assets without a resolved URL get an empty src (the
  // lightbox skips them gracefully).
  const lightboxImages = useMemo(() => {
    return linkedAssets.map((asset) => {
      const fileRecord = project?.boardFilesStore?.getById(asset.file) as any;
      const src = fileRecord?.url || fileRecord?.thumbnailUrl || "";
      return { src, alt: asset.altText || asset.caption || "" };
    });
  }, [linkedAssets, project, assetVersion]);

  const handleRemoveAsset = (assetId: number) => {
    const updated = watchedAssets.filter((id) => id !== assetId);
    setValue("assets", updated, { shouldDirty: true });
  };

  const handleAddImage = () => {
    if (!card) return;
    editorHandler.openEditor(
      EDITOR_KEYS.fileSelectorEditor,
      // @ts-expect-error - null recordIdObj when using by: "type"
      null,
      {
        otherParams: {
          title: "Select Marketing Assets",
          description: "Browse and add marketing assets as linked evidence",
          buttonLabel: "Save",
          getImages: (project: any) => {
            const problemHubId = (card as any)?.workFileRecord?.problemHubId ?? null;
            const assetStore = project.getCrudStore(STORE_KEYS.marketingAssetStore);
            const allAssets = (assetStore?.query(() => true) ?? []) as MarketingAsset[];
            const filteredAssets = problemHubId
              ? allAssets.filter((asset) => asset.tags?.includes(problemHubId))
              : allAssets;
            return filteredAssets
              .map((asset) => {
                const fileRecord = project.boardFilesStore?.getById(asset.file) as any;
                const url = fileRecord?.url || fileRecord?.thumbnailUrl || "";
                if (!url) return null;
                return {
                  id: asset.id,
                  url,
                  thumbnailUrl: url,
                  alt: asset.altText || asset.caption,
                } as SelectorImage;
              })
              .filter((img): img is SelectorImage => img !== null);
          },
          // Seed the modal selection from RHF form state (not card.assets).
          getSelectedImages: () => watchedAssets,
          onConfirm: (selected: SelectorImage[]) => {
            const newIds = selected.map((img) => img.id as number);
            const merged = [...new Set([...watchedAssets, ...newIds])];
            setValue("assets", merged, { shouldDirty: true });
          },
          onRemove: (id: string | number) => {
            const updated = watchedAssets.filter((assetId) => assetId !== id);
            setValue("assets", updated, { shouldDirty: true });
          },
        },
      },
      { by: "type" },
    );
  };

  // -------------------------------------------------------------------------
  // Save — only point where the Bryntum record is mutated
  // -------------------------------------------------------------------------
  const onValidSubmit = (values: CardFormValues) => {
    if (!onSave) return;
    // Commit text fields to the caller (which calls record.set).
    onSave({
      heading: values.heading,
      subHeading: values.subHeading,
      bodyText: values.bodyText,
    });
    // Commit assets directly on the record — kept separate so the caller's
    // onSave signature stays backward-compatible.
    if (card) {
      (card as any).set({ assets: values.assets });
    }
    // Mark form as pristine after a successful save.
    reset(values);
  };

  // -------------------------------------------------------------------------
  // Empty state
  // -------------------------------------------------------------------------
  if (!card && !isNewCard) {
    return (
      <main className="flex h-full min-h-0 flex-col overflow-hidden rounded-xl border border-border bg-background">
        <div className="sticky top-0 z-10 flex shrink-0 items-center gap-2 border-b border-border bg-card px-3 py-2">
          {/* Workfile context */}
          {file && (
            <>
              {onBack && (
                <button
                  type="button"
                  aria-label="Back to work files"
                  onClick={onBack}
                  className="inline-flex h-6 w-6 shrink-0 items-center justify-center rounded-md text-muted-foreground transition-colors hover:bg-muted hover:text-foreground"
                >
                  <ArrowLeft className="h-3.5 w-3.5" />
                </button>
              )}
              <div className="flex min-w-0 items-center gap-1.5">
                <span
                  className={cn(
                    "text-sm font-medium",
                    file.archived ? "text-muted-foreground line-through" : "text-foreground",
                  )}
                >
                  {file.title || "Untitled work file"}
                </span>
                {file.archived && (
                  <span className="inline-flex shrink-0 items-center gap-1 rounded-full border border-amber-500/40 bg-amber-500/10 px-1.5 py-0.5 text-[10px] font-medium text-amber-500">
                    <ArchiveIcon className="h-2.5 w-2.5" />
                    Archived
                  </span>
                )}
              </div>
              <ChevronRight className="h-3.5 w-3.5 shrink-0 text-border" />
            </>
          )}
          <span className="text-sm font-medium text-muted-foreground">No card selected</span>
        </div>
        <div className="flex flex-1 flex-col items-center justify-center px-6 py-12 text-center">
          <div className="mb-3 flex h-10 w-10 items-center justify-center rounded-md bg-muted text-muted-foreground">
            <LayoutGrid className="h-4 w-4" />
          </div>
          <p className="text-sm text-muted-foreground">Select a card to edit</p>
        </div>
      </main>
    );
  }

  const canSave = isDirty && isValid;

  return (
    <main className="flex h-full min-h-0 flex-col overflow-hidden rounded-xl border border-border bg-background">
      {/* Sticky sub-header */}
      <div className="sticky top-0 z-10 flex shrink-0 items-center justify-between border-b border-border bg-card px-3 py-2">
        {/* left — workfile context + card title */}
        <div className="flex min-w-0 items-center gap-2">
          {/* Workfile context prefix */}
          {file && (
            <>
              {onBack && (
                <button
                  type="button"
                  aria-label="Back to work files"
                  onClick={onBack}
                  className="inline-flex h-6 w-6 shrink-0 items-center justify-center rounded-md text-muted-foreground transition-colors hover:bg-muted hover:text-foreground"
                >
                  <ArrowLeft className="h-3.5 w-3.5" />
                </button>
              )}
              <div className="flex min-w-0 items-center gap-1.5">
                <span
                  className={cn(
                    "text-sm font-medium",
                    file.archived ? "text-muted-foreground line-through" : "text-foreground",
                  )}
                >
                  {file.title || "Untitled work file"}
                </span>
                {file.archived && (
                  <span className="inline-flex shrink-0 items-center gap-1 rounded-full border border-amber-500/40 bg-amber-500/10 px-1.5 py-0.5 text-[10px] font-medium text-amber-500">
                    <ArchiveIcon className="h-2.5 w-2.5" />
                    Archived
                  </span>
                )}
              </div>
              <ChevronRight className="h-3.5 w-3.5 shrink-0 text-border" />
            </>
          )}
          {/* Card title */}
          <span className="text-sm font-medium text-foreground">
            {isNewCard ? "New card" : (card?.heading?.trim() || "Untitled card")}
          </span>
        </div>

        {/* right — actions */}
        <div className="flex shrink-0 items-center justify-end gap-1.5">
          {isNewCard ? (
            <Button
              type="button"
              variant="ghost"
              size="sm"
              className="h-7 px-2.5 text-xs text-muted-foreground"
              onClick={onCancel}
            >
              Cancel
            </Button>
          ) : (
            <Button
              type="button"
              variant="ghost"
              size="icon"
              className="h-7 w-7 rounded-md text-muted-foreground hover:bg-destructive/10 hover:text-destructive"
              onClick={onDelete}
              aria-label="Delete card"
            >
              <Trash2 className="h-3.5 w-3.5" />
            </Button>
          )}
          <Button
            type="button"
            variant="default"
            size="sm"
            className="h-7 gap-1.5 px-2.5 text-xs"
            disabled={!canSave}
            onClick={handleSubmit(onValidSubmit)}
          >
            <Save className="h-3 w-3" />
            Save
          </Button>
        </div>
      </div>

      {/* Editable canvas — key remounts contentEditable DOM when card changes */}
      <div className="flex-1 overflow-y-auto">
        <article key={card?.id ?? "new"} className="mx-auto p-8">

          {/* Heading */}
          <Controller
            name="heading"
            control={control}
            rules={{ required: "Heading is required" }}
            render={({ field, fieldState }) => (
              <div>
                <h1
                  ref={field.ref}
                  contentEditable
                  suppressContentEditableWarning
                  spellCheck={false}
                  data-placeholder="Heading"
                  data-empty={!(card?.heading ?? "").trim() ? "" : undefined}
                  onInput={(e) => {
                    const el = e.currentTarget;
                    const text = el.innerText.trim();
                    const empty = text === "" || el.innerHTML === "<br>";
                    empty ? el.setAttribute("data-empty", "") : el.removeAttribute("data-empty");
                    field.onChange(text);
                  }}
                  className={cn(
                    "editable-field -mx-1 rounded-sm px-1 text-2xl font-semibold tracking-tight text-foreground focus:outline-none focus:ring-1 focus:ring-primary/40 mb-2",
                    fieldState.error && "ring-1 ring-destructive/50",
                  )}
                  dangerouslySetInnerHTML={{ __html: escapeHtml(card?.heading ?? "") }}
                />
              </div>
            )}
          />

          {/* Sub heading */}
          <Controller
            name="subHeading"
            control={control}
            rules={{ required: "Sub heading is required" }}
            render={({ field, fieldState }) => (
              <div className="mt-1">
                <p
                  ref={field.ref}
                  contentEditable
                  suppressContentEditableWarning
                  spellCheck={false}
                  data-placeholder="Sub heading"
                  data-empty={!(card?.subHeading ?? "").trim() ? "" : undefined}
                  onInput={(e) => {
                    const el = e.currentTarget;
                    const text = el.innerText.trim();
                    const empty = text === "" || el.innerHTML === "<br>";
                    empty ? el.setAttribute("data-empty", "") : el.removeAttribute("data-empty");
                    field.onChange(text);
                  }}
                  className={cn(
                    "editable-field -mx-1 rounded-sm px-1 text-base text-muted-foreground focus:outline-none focus:ring-1 focus:ring-primary/40",
                    fieldState.error && "ring-1 ring-destructive/50",
                  )}
                  dangerouslySetInnerHTML={{ __html: escapeHtml(card?.subHeading ?? "") }}
                />
              </div>
            )}
          />

          {/* Body text */}
          <Controller
            name="bodyText"
            control={control}
            rules={{ required: "Body text is required" }}
            render={({ field, fieldState }) => (
              <div className="mt-6">
                <p
                  ref={field.ref}
                  contentEditable
                  suppressContentEditableWarning
                  spellCheck={false}
                  data-placeholder="Body text…"
                  data-empty={!(card?.bodyText ?? "").trim() ? "" : undefined}
                  onInput={(e) => {
                    const el = e.currentTarget;
                    const text = el.innerText;
                    const empty = text.trim() === "" || el.innerHTML === "<br>";
                    empty ? el.setAttribute("data-empty", "") : el.removeAttribute("data-empty");
                    // Preserve newlines for body text — don't trim.
                    field.onChange(text);
                  }}
                  className={cn(
                    "editable-field -mx-1 min-h-[1.75rem] whitespace-pre-wrap rounded-sm px-1 leading-relaxed text-foreground/90 focus:outline-none focus:ring-1 focus:ring-primary/40",
                    fieldState.error && "ring-1 ring-destructive/50",
                  )}
                  dangerouslySetInnerHTML={{ __html: escapeHtml(card?.bodyText ?? "") }}
                />
              </div>
            )}
          />

          {/* Assets */}
          <LightboxProvider images={lightboxImages} showThumbnails allowZoom>
            <div className="mt-6 flex flex-wrap gap-3">
              {linkedAssets.map((asset, idx) => (
                <AssetTile
                  key={asset.id}
                  asset={asset}
                  lightboxIndex={idx}
                  onRemove={() => handleRemoveAsset(asset.id as number)}
                />
              ))}
              <button
                type="button"
                onClick={handleAddImage}
                className={cn(
                  "group flex h-36 w-36 shrink-0 flex-col items-center justify-center gap-2 rounded-lg border-2 border-dashed border-border bg-muted/30 text-muted-foreground transition-colors",
                  "hover:border-primary hover:bg-primary/5 hover:text-primary",
                )}
              >
                <span className="flex h-9 w-9 items-center justify-center rounded-full border border-border bg-background shadow-sm transition-colors group-hover:border-primary group-hover:text-primary">
                  <Plus className="h-4 w-4" />
                </span>
                <span className="text-xs font-medium">Add image</span>
              </button>
            </div>
          </LightboxProvider>
        </article>
      </div>
    </main>
  );
};

interface AssetTileProps {
  asset: MarketingAsset;
  onRemove: () => void;
  lightboxIndex: number;
}

const AssetTile = ({ asset, onRemove, lightboxIndex }: AssetTileProps) => {
  const { project } = useSwivuBryntumContext();
  const { openLightbox } = useLightboxContext();
  const fileRecord = project?.boardFilesStore?.getById(asset.file) as any;
  const thumbnailUrl = fileRecord?.url || fileRecord?.thumbnailUrl;

  return (
    <div className="group relative overflow-hidden rounded-lg border border-border bg-muted">
      {thumbnailUrl ? (
        <img
          src={thumbnailUrl}
          alt={asset.altText || asset.caption || ""}
          className="block h-36 w-36 cursor-pointer object-cover"
          onClick={() => openLightbox(lightboxIndex)}
        />
      ) : (
        <div className="flex h-36 w-36 items-center justify-center">
          <ImageIcon className="h-6 w-6 text-muted-foreground" />
        </div>
      )}
      <button
        type="button"
        onClick={onRemove}
        className="absolute right-1.5 top-1.5 z-10 flex h-6 w-6 items-center justify-center rounded-full bg-background/80 text-muted-foreground opacity-0 shadow-sm transition-opacity hover:bg-destructive hover:text-white group-hover:opacity-100"
        aria-label="Remove asset"
      >
        <X className="h-3 w-3" />
      </button>
    </div>
  );
};

export default WorkFileLivePreview;
